import {getAuthToken} from "@/utils/auth-token";
import {canAccessRoute, normalizeAccessContext, normalizeRouteMeta, resolveStoreAccess} from "@/utils/access-context";
import {getNavigationItems, resolveNavigationRouteForAccess} from "@/utils/navigation-items";

export const NAVIGATION_SHORTCUT_MENU_TARGETS = getNavigationItems().map((item) => ({
    ...item,
    canAccess(context) {
        return !!resolveNavigationRouteForAccess(item, context.access);
    },
}));

const MENU_TARGETS_BY_ID = NAVIGATION_SHORTCUT_MENU_TARGETS.reduce((accumulator, item) => {
    accumulator[item.id] = item;
    return accumulator;
}, {});

const MODIFIER_KEYS = new Set(['control', 'ctrl', 'meta', 'shift', 'alt']);
const SPECIAL_KEYS = {
    escape: 'esc',
    ' ': 'space',
    spacebar: 'space',
    arrowup: 'arrowup',
    arrowdown: 'arrowdown',
    arrowleft: 'arrowleft',
    arrowright: 'arrowright',
    pageup: 'pageup',
    pagedown: 'pagedown',
    home: 'home',
    end: 'end',
    insert: 'insert',
    delete: 'delete',
    backspace: 'backspace',
    enter: 'enter',
    tab: 'tab',
};

const LEADER_TIMEOUT_MS = 1000;

let leaderUiState = {
    active: false,
    label: '',
};

const leaderUiSubscribers = new Set();

function emitLeaderUiState() {
    leaderUiSubscribers.forEach((listener) => {
        try {
            listener({...leaderUiState});
        } catch (error) {
        }
    });
}

function setLeaderUiState(nextState) {
    leaderUiState = {
        ...leaderUiState,
        ...nextState,
    };

    emitLeaderUiState();
}

export function subscribeNavigationShortcutLeaderState(listener) {
    if (typeof listener !== 'function') {
        return () => {
        };
    }

    leaderUiSubscribers.add(listener);
    listener({...leaderUiState});

    return () => {
        leaderUiSubscribers.delete(listener);
    };
}

function isMacPlatform() {
    if (typeof navigator === 'undefined') {
        return false;
    }

    return /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent || '');
}

function isMobileShortcutEnvironment() {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return false;
    }

    const userAgent = navigator.userAgent || '';
    const mobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    const ipadLikeDevice = navigator.platform === 'MacIntel' && Number(navigator.maxTouchPoints || 0) > 1;
    const smallViewport = typeof window.matchMedia === 'function'
        ? window.matchMedia('(max-width: 1023px)').matches
        : window.innerWidth < 1024;
    const coarsePointer = typeof window.matchMedia === 'function'
        ? window.matchMedia('(pointer: coarse)').matches
        : false;

    return mobileUserAgent || ipadLikeDevice || (smallViewport && coarsePointer);
}

function normalizeKeyName(key) {
    if (key === null || key === undefined) {
        return null;
    }

    const rawKey = String(key);
    const normalized = rawKey.toLowerCase();

    if (MODIFIER_KEYS.has(normalized)) {
        return null;
    }

    if (rawKey.length === 1) {
        if (/\s/.test(rawKey)) {
            return rawKey === ' ' ? 'space' : null;
        }

        return normalized;
    }

    if (SPECIAL_KEYS[normalized]) {
        return SPECIAL_KEYS[normalized];
    }

    if (/^f\d{1,2}$/i.test(rawKey)) {
        return normalized;
    }

    return normalized !== '' ? normalized : null;
}

export function normalizeNavigationShortcutKey(key) {
    return normalizeKeyName(key);
}

export function normalizeRelativeRoute(route) {
    const value = String(route || '').trim();
    if (value === '') {
        return null;
    }

    if (!value.startsWith('/')) {
        return null;
    }

    if (/^(?:[a-z][a-z0-9+\-.]*:)?\/\//i.test(value)) {
        return null;
    }

    return value;
}

export function shortcutKeyFromKeyboardEvent(event) {
    if (!event) {
        return null;
    }

    if (event.ctrlKey || event.metaKey || event.altKey) {
        return null;
    }

    return normalizeKeyName(event.key);
}

export function getNavigationShortcutLeaderLabel() {
    return isMacPlatform() ? '⌘' : 'Ctrl';
}

export function getNavigationShortcutKeyDisplayLabel(key) {
    const normalizedKey = normalizeNavigationShortcutKey(key);
    if (!normalizedKey) {
        return '';
    }

    if (normalizedKey === 'esc') {
        return 'Esc';
    }
    if (normalizedKey === 'space') {
        return 'Space';
    }
    if (/^f\d{1,2}$/.test(normalizedKey)) {
        return normalizedKey.toUpperCase();
    }
    if (normalizedKey.length === 1) {
        return normalizedKey.toUpperCase();
    }

    return normalizedKey.charAt(0).toUpperCase() + normalizedKey.slice(1);
}

function normalizeShortcutItem(item) {
    if (!item || typeof item !== 'object') {
        return null;
    }

    const key = normalizeNavigationShortcutKey(item.key);
    const targetType = String(item.target_type || '').trim();
    const label = String(item.label || '').trim();

    if (!key || (targetType !== 'menu' && targetType !== 'route')) {
        return null;
    }

    let targetValue = String(item.target_value || '').trim();
    if (targetType === 'menu') {
        if (!MENU_TARGETS_BY_ID[targetValue]) {
            return null;
        }
    } else {
        targetValue = normalizeRelativeRoute(targetValue) || '';
        if (targetValue === '') {
            return null;
        }
    }

    return {
        enabled: item.enabled === undefined ? true : !!item.enabled,
        label: label !== '' ? label : null,
        key,
        target_type: targetType,
        target_value: targetValue,
    };
}

export function normalizeNavigationShortcutConfig(config) {
    let value = config;

    if (typeof value === 'string' && value.trim() !== '') {
        try {
            value = JSON.parse(value);
        } catch (error) {
            value = null;
        }
    }

    if (!value || typeof value !== 'object') {
        return {
            enabled: true,
            items: [],
        };
    }

    const rawItems = Array.isArray(value.items) ? value.items : [];
    const usedKeys = new Set();
    const items = [];

    rawItems.forEach((item) => {
        const normalizedItem = normalizeShortcutItem(item);
        if (!normalizedItem) {
            return;
        }

        if (usedKeys.has(normalizedItem.key)) {
            return;
        }

        usedKeys.add(normalizedItem.key);
        items.push(normalizedItem);
    });

    return {
        enabled: value.enabled === undefined ? true : !!value.enabled,
        items,
    };
}

function buildAccessContext(store) {
    return {
        access: resolveStoreAccess(store),
    };
}

function isShortcutInputTarget(target) {
    if (!target || !(target instanceof HTMLElement)) {
        return false;
    }

    if (target.isContentEditable) {
        return true;
    }

    return !!target.closest('input, textarea, [contenteditable="true"], [role="textbox"], .tox, .CodeMirror, .monaco-editor, .ql-editor, .ProseMirror');
}

function mergeRouteMeta(route) {
    return (route && Array.isArray(route.matched) ? route.matched : []).reduce((meta, record) => {
        return {
            ...meta,
            ...(record && record.meta ? record.meta : {}),
        };
    }, {});
}

function canAccessResolvedRoute(route, store) {
    if (!route || !Array.isArray(route.matched) || route.matched.length === 0) {
        return false;
    }

    const context = buildAccessContext(store);
    const meta = normalizeRouteMeta(mergeRouteMeta(route));
    return canAccessRoute({
        path: String(route.path || ''),
        meta,
        access: context.access,
        hasToken: !!getAuthToken(),
    });
}

function resolveMenuTargetRoute(targetId, store) {
    const target = MENU_TARGETS_BY_ID[targetId];
    if (!target) {
        return null;
    }

    const context = buildAccessContext(store);
    if (typeof target.canAccess === 'function' && !target.canAccess(context)) {
        return null;
    }

    return resolveNavigationRouteForAccess(target, context.access);
}

function resolveShortcutTarget(item, router, store) {
    if (!item || !router) {
        return null;
    }

    if (item.target_type === 'menu') {
        return resolveMenuTargetRoute(item.target_value, store);
    }

    const routePath = normalizeRelativeRoute(item.target_value);
    if (!routePath) {
        return null;
    }

    const resolvedRoute = router.resolve(routePath);
    if (!canAccessResolvedRoute(resolvedRoute, store)) {
        return null;
    }

    return resolvedRoute.fullPath || resolvedRoute.path || null;
}

function getSettingsSource(store) {
    return (store && store.state && store.state.settings)
        ? store.state.settings
        : (window.app || {});
}

export function getNavigationShortcutBadgesForTarget({store, targetId = null, routePath = null}) {
    const settings = getSettingsSource(store);
    const config = normalizeNavigationShortcutConfig(settings.navigation_shortcuts);
    const normalizedRoute = routePath ? normalizeRelativeRoute(routePath) : null;

    return config.items
        .filter((item) => item.enabled !== false)
        .filter((item) => {
            if (targetId && item.target_type === 'menu') {
                return item.target_value === targetId;
            }

            if (normalizedRoute && item.target_type === 'route') {
                return normalizeRelativeRoute(item.target_value) === normalizedRoute;
            }

            return false;
        })
        .map((item) => ({
            key: item.key,
            label: getNavigationShortcutKeyDisplayLabel(item.key),
            title: item.label || null,
        }))
        .sort((left, right) => left.label.localeCompare(right.label, 'de'));
}

function isPrimaryModifierEvent(event) {
    if (!event) {
        return false;
    }

    const mac = isMacPlatform();
    if (mac) {
        return event.key === 'Meta' && !event.ctrlKey && !event.altKey && !event.shiftKey;
    }

    return event.key === 'Control' && !event.metaKey && !event.altKey && !event.shiftKey;
}

export function registerNavigationShortcuts({router, store}) {
    if (isMobileShortcutEnvironment()) {
        setLeaderUiState({
            active: false,
            label: getNavigationShortcutLeaderLabel(),
        });

        return function unregisterNavigationShortcuts() {
        };
    }

    let leaderModifierPressed = false;
    let leaderModifierUsed = false;
    let leaderModeActive = false;
    let leaderTimer = null;
    const wheelListenerOptions = {passive: true};

    const closeLeaderMode = () => {
        leaderModeActive = false;
        if (leaderTimer) {
            window.clearTimeout(leaderTimer);
            leaderTimer = null;
        }
        setLeaderUiState({
            active: false,
            label: getNavigationShortcutLeaderLabel(),
        });
    };

    const openLeaderMode = () => {
        leaderModeActive = true;
        if (leaderTimer) {
            window.clearTimeout(leaderTimer);
        }
        leaderTimer = window.setTimeout(() => {
            closeLeaderMode();
        }, LEADER_TIMEOUT_MS);

        setLeaderUiState({
            active: true,
            label: getNavigationShortcutLeaderLabel(),
        });
    };

    const toggleLeaderMode = () => {
        if (leaderModeActive) {
            closeLeaderMode();
            return;
        }

        const settings = getSettingsSource(store);
        const config = normalizeNavigationShortcutConfig(settings.navigation_shortcuts);
        if (!config.enabled || config.items.length === 0) {
            return;
        }

        openLeaderMode();
    };

    const handleKeydown = (event) => {
        if (event.defaultPrevented || event.repeat) {
            return;
        }

        if (isPrimaryModifierEvent(event)) {
            leaderModifierPressed = true;
            return;
        }

        if (leaderModifierPressed) {
            leaderModifierUsed = true;
        }

        if (!leaderModeActive) {
            return;
        }

        if (isShortcutInputTarget(event.target)) {
            closeLeaderMode();
            return;
        }

        if (event.key === 'Escape') {
            event.preventDefault();
            closeLeaderMode();
            return;
        }

        const key = shortcutKeyFromKeyboardEvent(event);
        if (!key) {
            return;
        }

        event.preventDefault();

        const settings = getSettingsSource(store);
        const config = normalizeNavigationShortcutConfig(settings.navigation_shortcuts);
        const item = config.items.find((entry) => entry.enabled !== false && entry.key === key);

        closeLeaderMode();

        if (!item) {
            return;
        }

        const targetRoute = resolveShortcutTarget(item, router, store);
        if (!targetRoute) {
            return;
        }

        const currentPath = router && router.currentRoute && router.currentRoute.value
            ? router.currentRoute.value.fullPath || router.currentRoute.value.path
            : '';
        if (currentPath === targetRoute) {
            return;
        }

        router.push(targetRoute).catch(() => {
        });
    };

    const handleKeyup = (event) => {
        if (!isPrimaryModifierEvent(event)) {
            return;
        }

        const shouldToggleLeader = leaderModifierPressed
            && !leaderModifierUsed
            && !isShortcutInputTarget(event.target);

        leaderModifierPressed = false;
        leaderModifierUsed = false;

        if (!shouldToggleLeader) {
            return;
        }

        toggleLeaderMode();
    };

    const handlePointerDown = () => {
        if (leaderModifierPressed) {
            leaderModifierUsed = true;
        }

        if (leaderModeActive) {
            closeLeaderMode();
        }
    };

    const handleWheel = () => {
        if (leaderModifierPressed) {
            leaderModifierUsed = true;
        }
    };

    const handleWindowBlur = () => {
        leaderModifierPressed = false;
        leaderModifierUsed = false;
        closeLeaderMode();
    };

    const handleVisibilityChange = () => {
        if (document.hidden) {
            handleWindowBlur();
        }
    };

    setLeaderUiState({
        active: false,
        label: getNavigationShortcutLeaderLabel(),
    });

    window.addEventListener('keydown', handleKeydown);
    window.addEventListener('keyup', handleKeyup);
    window.addEventListener('pointerdown', handlePointerDown, true);
    window.addEventListener('wheel', handleWheel, wheelListenerOptions);
    window.addEventListener('blur', handleWindowBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return function unregisterNavigationShortcuts() {
        closeLeaderMode();
        window.removeEventListener('keydown', handleKeydown);
        window.removeEventListener('keyup', handleKeyup);
        window.removeEventListener('pointerdown', handlePointerDown, true);
        window.removeEventListener('wheel', handleWheel, wheelListenerOptions);
        window.removeEventListener('blur', handleWindowBlur);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
}
